import { HOLIDAY_RULES } from '../../data/holidays-de';
import { PRODUCTS } from '../../data/products';
import { addDays, daysBetween, type IsoDate } from '../../domain/dates';
import { holidayOn, isClosedDay } from '../../domain/holidays';
import { toCents, toEuros } from '../../domain/money';
import type { MeatAddOn, PlannedDinner } from '../../domain/plan';
import { createCatalog, productOf } from '../../domain/price-book';
import { isFishDinner } from '../../domain/recipe';
import { ownProductPicture } from '../pictures/pictures';
import { recipeById } from '../recipes/recipe-lookup';
import { CATEGORY_ORDER } from '../shopping/shopping-view';
import type { ShoppingItem } from '../../domain/shopping';
import type { Product, StoreId } from '../../domain/types';
import type { AppData, StoredTrip } from '../state/app-data';
import { currentPlan, nextPlan } from '../state/plan-service';

export interface StoreRow {
  storeId: StoreId;
  items: number;
  total: number;
}

export type ShoppingToday =
  | {
      kind: 'trip';
      extra: boolean;
      stores: StoreRow[];
      total: number;
      wineTotal: number;
      estimated: boolean;
      /**
       * DE: Bis zu sechs Produkte mit eigenem Bild für den Streifen „Серед покупок“.
       * EN: Up to six products with a picture of their own for the "Among the shopping" strip.
       */
      strip: string[];
    }
  | {
      kind: 'none';
      closedToday: boolean;
      nextTrip?: { date: IsoDate; storeIds: StoreId[]; total: number };
    };

export interface DinnerToday {
  recipeId: string;
  activeMinutes: number;
  totalMinutes: number;
  servings: number;
  fish: boolean;
  addOns: MeatAddOn[];
  allAtHome: boolean;
  /**
   * DE: Produkte für heute, die vor dem nächsten Einkauf fehlen, weil die Läden zu sind.
   * EN: Products for today that are missing before the next trip because the shops are closed.
   */
  missing: string[];
}

/**
 * DE: Alles, was der Bildschirm „Heute“ zeigt: Einkauf, Abendessen, morgen und Hinweise.
 * EN: Everything the Today screen shows: shopping, dinner, tomorrow and hints.
 */
export interface TodaySummary {
  shopping: ShoppingToday;
  dinner: DinnerToday | undefined;
  tomorrow: { recipeId: string; totalMinutes: number; fish: boolean } | undefined;
  holidayTomorrow: string | undefined;
  needsPlan: boolean;
  offerNextPlan: boolean;
  askFederalState: boolean;
}

// DE: In den letzten drei Tagen eines Plans wird der nächste angeboten.
// EN: The next plan is offered during the last three days of a plan.
const NEXT_PLAN_DAYS = 3;

const sumEuros = (items: readonly ShoppingItem[]) =>
  toEuros(items.reduce((sum, entry) => sum + toCents(entry.total), 0));

function storeRows(items: readonly ShoppingItem[], storeIds: readonly StoreId[]): StoreRow[] {
  return storeIds
    .map((storeId) => items.filter((entry) => entry.storeId === storeId))
    .filter((storeItems) => storeItems.length > 0)
    .map((storeItems) => ({
      storeId: storeItems[0]?.storeId as StoreId,
      items: storeItems.length,
      total: sumEuros(storeItems),
    }));
}

const STRIP_SIZE = 6;

// DE: Produkte mit eigenem Bild in Ladenreihenfolge, jedes nur einmal.
// EN: Products with a picture of their own in shop order, each only once.
function stripOf(items: readonly ShoppingItem[], catalog: ReadonlyMap<string, Product>): string[] {
  const pictured = [...new Set(items.map((entry) => entry.productId))].filter(
    (productId) => ownProductPicture(productId) !== undefined,
  );
  const rank = (productId: string) =>
    CATEGORY_ORDER.indexOf(productOf(catalog, productId).category);
  return pictured.sort((a, b) => rank(a) - rank(b)).slice(0, STRIP_SIZE);
}

const isOpen = (trip: StoredTrip) => !trip.bought && trip.items.length > 0;

function shoppingToday(
  data: AppData,
  today: IsoDate,
  catalog: ReadonlyMap<string, Product>,
): ShoppingToday {
  const plans = [currentPlan(data, today), nextPlan(data, today)];
  const trips = plans
    .flatMap((stored) => stored?.trips ?? [])
    .sort((a, b) => a.date.localeCompare(b.date));
  const closedToday = isClosedDay(today, { ...data.household, holidays: HOLIDAY_RULES });
  // DE: An einem Ruhetag schickt die App niemanden einkaufen, auch wenn noch ein Einkauf gespeichert ist.
  // EN: On a closed day the app sends nobody shopping, even if a trip is still stored.
  const todays = closedToday ? [] : trips.filter((trip) => trip.date === today);
  const open = todays.filter(isOpen).flatMap((trip) => trip.items);
  if (open.length > 0) {
    const wine = open.filter((entry) =>
      productOf(catalog, entry.productId).tags?.includes('alcohol'),
    );
    return {
      kind: 'trip',
      extra: todays.some((trip) => trip.bought),
      stores: storeRows(open, data.household.storeIds),
      total: sumEuros(open),
      wineTotal: sumEuros(wine),
      estimated: open.some((entry) => entry.estimated),
      strip: stripOf(open, catalog),
    };
  }
  const next = trips.find((trip) => trip.date > today && isOpen(trip));
  if (!next) return { kind: 'none', closedToday };
  const storeIds = storeRows(next.items, data.household.storeIds).map((row) => row.storeId);
  return { kind: 'none', closedToday, nextTrip: { date: next.date, storeIds, total: next.total } };
}

// DE: Der geplante Tag und was dafür fehlt, weil die Läden vorher zu sind.
// EN: The planned day and what it lacks because the shops are closed before it.
function planOn(data: AppData, date: IsoDate) {
  const plan = currentPlan(data, date)?.plan;
  const warning = plan?.warnings.find(
    (entry) => entry.code === 'shops-closed' && entry.date === date,
  );
  return {
    day: plan?.days.find((planned) => planned.date === date),
    missing: warning?.productIds ?? [],
  };
}

/**
 * DE: In den letzten Tagen eines Plans ohne Folgeplan wird der nächste angeboten.
 * EN: During a plan's last days without a following plan the next one is offered.
 */
export function offersNextPlan(data: AppData, today: IsoDate): boolean {
  const current = currentPlan(data, today);
  if (!current) return false;
  const lastDay = addDays(current.period.start, current.period.days - 1);
  return !nextPlan(data, today) && daysBetween(today, lastDay) < NEXT_PLAN_DAYS;
}

/**
 * DE: Zusammenfassung für heute aus den gespeicherten Plänen (laufender und nächster Plan).
 * EN: Summary for today from the stored plans (running and next plan).
 */
export function todaySummary(data: AppData, today: IsoDate): TodaySummary {
  const catalog = createCatalog(PRODUCTS);
  const shopping = shoppingToday(data, today, catalog);
  const tomorrowDate = addDays(today, 1);
  const { day, missing } = planOn(data, today);
  const next = planOn(data, tomorrowDate).day;
  return {
    shopping,
    dinner: day && dinnerToday(day.dinner, catalog, shopping.kind, missing),
    tomorrow: next && tomorrowOf(next.dinner.recipeId, catalog),
    holidayTomorrow: holidayOn(tomorrowDate, { ...data.household, holidays: HOLIDAY_RULES }),
    // DE: Ein Plan, der erst morgen beginnt (z. B. nach einem Sonntag), zählt schon.
    // EN: A plan that starts tomorrow (e.g. after a Sunday) already counts.
    needsPlan: !currentPlan(data, today) && !nextPlan(data, today),
    offerNextPlan: offersNextPlan(data, today),
    askFederalState: data.household.federalState === undefined,
  };
}

function dinnerToday(
  dinner: PlannedDinner,
  catalog: ReadonlyMap<string, Product>,
  shopping: ShoppingToday['kind'],
  missing: string[],
): DinnerToday {
  const recipe = recipeById(dinner.recipeId);
  return {
    recipeId: recipe.id,
    activeMinutes: recipe.activeMinutes,
    totalMinutes: recipe.totalMinutes,
    servings: dinner.servings,
    fish: isFishDinner(recipe, catalog),
    addOns: dinner.meatAddOns,
    // DE: „Alles da“ nur ohne Einkauf heute und ohne Lücke an einem Ruhetag.
    // EN: "All at home" only without a trip today and without a gap on a closed day.
    allAtHome: shopping === 'none' && missing.length === 0,
    missing,
  };
}

function tomorrowOf(recipeId: string, catalog: ReadonlyMap<string, Product>) {
  const recipe = recipeById(recipeId);
  return { recipeId, totalMinutes: recipe.totalMinutes, fish: isFishDinner(recipe, catalog) };
}
